import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CustomButton } from '../Button/Button';
import { Icon } from '../Icon';
import styles from './TimeInput.module.scss';
import TimeField from './TimeField';
import cx from 'classnames';
import { v4 as uuidv4 } from 'uuid';
import { roundToDigit } from '../../utils';
import { convertSeconds } from '../../utils/TimeUtil';
import { useTranslation } from 'react-i18next';

const initDisableControls = {
  increment: false,
  decrement: false,
};

const TimeInput = ({
  label = '',
  variant = 'basic',
  onChange,
  value,
  min = 0,
  max,
  disabled = false,
  showControls = true,
  disabledControls = initDisableControls,
  inputClassName,
  containerClassName,
  registerAction = () => { },
  step = 100,
}) => {
  const [formattedTime, setFormattedTime] = useState('');
  const [isBoundaries, setIsBoundaries] = useState(false);

  const { t } = useTranslation();

  const intervalRef = useRef(null);
  const delayTimeoutRef = useRef(null);
  const roundedMax = useMemo(() => roundToDigit(max), [max]);
  const roundedMin = useMemo(() => roundToDigit(min), [min]);
  const roundedValue = useMemo(() => roundToDigit(value), [value]);

  const inputId = useMemo(() => uuidv4(), []);

  const time = useRef({ hours: 0, minutes: 0, seconds: 0, milliseconds: 0 });

  useEffect(() => {
    if (value || value === 0) {
      const converted = convertSeconds(value);
      formattedTime !== converted && validateFormattedTime(converted);
    }
  }, [value, min, max]);

  const splitSecondString = seconds => {
    return seconds
      .replace(/\./g, ':')
      .split(':')
      .map(item => Number(item));
  };

  const countSecondsFromArray = array => {
    const secondsArray = array
      .slice(0, 3)
      .reverse()
      .reduce((accumulator, currentValue, index, array) => accumulator + currentValue * 60 ** index);
    return secondsArray + array[array.length - 1] / 1000;
  };

  const validateFormattedTime = value => {
    const timeArray = splitSecondString(value);
    const totalValue = countSecondsFromArray(timeArray);
    if (roundToDigit(totalValue) > roundedMax) {
      const formattedMaxValue = convertSeconds(max);
      if (formattedMaxValue === formattedTime) {
        setFormattedTime('');
      }
      setTimeout(() => {
        setFormattedTime(formattedMaxValue);
      }, 0);
      const maxValueArray = splitSecondString(formattedMaxValue);
      time.current.hours = maxValueArray[0];
      time.current.minutes = maxValueArray[1];
      time.current.seconds = maxValueArray[2];
      time.current.milliseconds = maxValueArray[3];
      setIsBoundaries(true);
    } else if (roundToDigit(totalValue) < roundedMin) {
      const formattedMinValue = convertSeconds(min);
      if (formattedMinValue === formattedTime) {
        setFormattedTime('');
      }
      setTimeout(() => {
        setFormattedTime(formattedMinValue);
      }, 0);
      const minValueArray = splitSecondString(formattedMinValue);
      time.current.hours = minValueArray[0];
      time.current.minutes = minValueArray[1];
      time.current.seconds = minValueArray[2];
      time.current.milliseconds = minValueArray[3];
      setIsBoundaries(true);
    } else {
      setIsBoundaries(false);
      setFormattedTime(value);
      time.current.hours = timeArray[0];
      time.current.minutes = timeArray[1];
      time.current.seconds = timeArray[2];
      time.current.milliseconds = timeArray[3];
    }
  };

  const handleSecondsUpdate = () => {
    const secondsToUpdate = countSecondsFromArray(Array.from(Object.values(time.current)));
    onChange(secondsToUpdate);
  };

  const handleChange = event => {
    console.log('event.target.value', event.target.value);
    validateFormattedTime(event.target.value);
    handleSecondsUpdate();
  };

  const handleIncrement = () => {
    if (time.current.milliseconds - step < 1000) {
      time.current.milliseconds += step;
    } else {
      time.current.milliseconds -= 1000 - step;
      if (time.current.seconds < 59) {
        time.current.seconds += 1;
      } else {
        if (time.current.minutes < 59) {
          time.current.minutes += 1;
          time.current.seconds = 0;
        } else {
          time.current.hours += 1;
          time.current.minutes = 0;
          time.current.seconds = 0;
        }
      }
    }
    const incrementedValue = `${Array.from(Object.values(time.current))
      .slice(0, 3)
      .map(item => item.toString().padStart(2, '0'))
      .join(':')}.${time.current.milliseconds.toString().padStart(3, '0')}`;
    validateFormattedTime(incrementedValue);
    handleSecondsUpdate();
    registerAction();
  };

  const handleDecrement = () => {
    if (time.current.milliseconds > 0 || time.current.seconds > 0 || time.current.minutes > 0 || time.current.hours > 0) {
      if (time.current.milliseconds - step >= 0) {
        console.log('time.current.milliseconds - step >= 0', time.current.milliseconds);
        time.current.milliseconds -= step;
      } else {
        console.log('time.current.milliseconds - step < 0', time.current.milliseconds);
        if (time.current.seconds > 0) {
          time.current.milliseconds += 1000 - step;
          time.current.seconds -= 1;
        } else {
          time.current.milliseconds = 0;
          if (time.current.minutes > 0) {
            time.current.minutes -= 1;
            time.current.seconds = 59;
          } else {
            if (time.current.hours > 0) {
              time.current.hours -= 1;
              time.current.minutes = 59;
              time.current.seconds = 59;
            } else {
              time.current.hours = 0;
              time.current.minutes = 0;
              time.current.seconds = 0;
              time.current.milliseconds = 0;
            }
          }
        }
      }
    }
    const decrementedValue = `${Array.from(Object.values(time.current))
      .slice(0, 3)
      .map(item => item.toString().padStart(2, '0'))
      .join(':')}.${time.current.milliseconds.toString().padStart(3, '0')}`;
    validateFormattedTime(decrementedValue);
    handleSecondsUpdate();
    registerAction();
  };

  const handleSetInterval = useCallback(
    (increment = false) => {
      if (delayTimeoutRef.current) return;
      if (increment) {
        handleIncrement();
      } else {
        handleDecrement();
      }
      delayTimeoutRef.current = setTimeout(() => delayTimeoutCallback(increment), 300);
    },
    [delayTimeoutRef, handleIncrement, handleDecrement]
  );

  const handleClearInterval = () => {
    clearInterval(intervalRef.current);
    clearTimeout(delayTimeoutRef.current);
    intervalRef.current = null;
    delayTimeoutRef.current = null;
  };

  const delayTimeoutCallback = increment => {
    if (intervalRef.current) return;
    intervalRef.current = setInterval(() => {
      if (increment) {
        handleIncrement();
      } else {
        handleDecrement();
      }
    }, 100);
  };

  useEffect(() => {
    if (roundedValue <= roundedMin || roundedValue >= roundedMax) {
      handleClearInterval();
    }
  }, [roundedValue]);

  return (
    <div className={cx(styles.inputContainer, containerClassName)}>
      <label htmlFor={`${inputId}_input_id`} className={styles.inputLabel} style={{ marginRight: label ? '16px' : '' }}>
        {label}
      </label>
      <TimeField
        disabled={disabled}
        value={formattedTime}
        onChange={handleChange}
        showSeconds={true}
        boundaries={isBoundaries}
        input={
          <input
            id={`${inputId}_input_id`}
            type="text"
            className={cx(inputClassName, styles.input, styles[variant], {
              [styles.withControls]: showControls,
              [styles.disabled]: disabled,
            })}
            onBlur={registerAction}
            title={`${inputId}_input_id`}
          />
        }
      />

      {showControls && (
        <div className={styles.buttonsContainer}>
          <CustomButton
            variant={'link'}
            className={styles.customizeBtn}
            onMouseDown={() => handleSetInterval(true)}
            onMouseUp={handleClearInterval}
            onMouseLeave={handleClearInterval}
            disabled={roundedValue >= roundedMax || disabledControls?.increment}
            ariaLabel={t('accessibility.increment')}
          >
            <Icon src={'increment'} verticalAlign="middle" className={styles.icon} />
          </CustomButton>
          <CustomButton
            variant={'link'}
            className={styles.customizeBtn}
            onMouseDown={() => handleSetInterval(false)}
            onMouseUp={handleClearInterval}
            onMouseLeave={handleClearInterval}
            disabled={roundedValue <= roundedMin || disabledControls?.decrement}
            ariaLabel={t('accessibility.decrement')}
          >
            <Icon src={'decrement'} verticalAlign="middle" className={styles.icon} />
          </CustomButton>
        </div>
      )}
    </div>
  );
};

export default TimeInput;
